import React, { useState, useEffect, useRef, useId } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { showToast } from './Toast';
import { useAnalytics } from '../hooks/useAnalytics';
import { formatCents } from '../utils/introOffer';
import { useOfferVariant } from '../hooks/useOfferVariant';
import { cardOffer, paywallKind, annualSavingsLabel, pricesFrom, priceable, offerAnalytics, servedOfferVariant, winbackCard, PLAN_COPY } from '../utils/membershipOffer';
import { TrialTimeline, ReviewCard, PriceField, PriceHint } from './TrialWall';
import { FunnelPriceChooser } from './funnel/FunnelPriceChooser';
import { MembershipCheckout } from './MembershipCheckout';
import CancelFlow from './CancelFlow';

// The Settings / navigation subscription modal: members change or cancel
// their plan here; non-members get the same three paywalls as the website
// wall (utils/membershipOffer) — the normal offer priced from
// /subscription-plans, the win-back offer from the win-back email, or a
// Moments buyer's trial. No price lives in this file.

// The first-month price a parent named in the pay-what-you-want arm, kept for
// the tab (shared with the website wall, so the two never disagree)
const CHOSEN_CENTS_KEY = 'pixley_web_paywall_chosen';
const readChosenCents = () => {
  try {
    const v = Number(sessionStorage.getItem(CHOSEN_CENTS_KEY));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch (_) {
    return null;
  }
};
const writeChosenCents = (cents) => {
  try {
    sessionStorage.setItem(CHOSEN_CENTS_KEY, String(cents));
  } catch (_) {
    // not kept across a reload; the suggested price shows again
  }
};

const OpenSubscriptionModal = ({
  onClose, apiClient, user,
  highlightTier = null,
  offer = null, // "winback" (from the win-back email): 33% off 3 monthly invoices
}) => {
  const { subscriptionStatus, waitForSubscriptionActivation, refreshSubscriptionStatus } = useAuth();
  const { requireParentalVerification } = useProfile();
  const { trackCheckoutStarted } = useAnalytics();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelFlow, setShowCancelFlow] = useState(false);

  const getCurrentPlanKey = () => {
    const tier = subscriptionStatus?.subscriptionType;
    const tierMap = { 'NONE': 'free', 'STARTER': 'starter', 'PLUS': 'plus', 'PRO': 'plus', 'FAMILY': 'family', 'UNLIMITED': 'unlimited', 'PREMIUM': 'plus', 'EDDY': 'plus', 'PHILIP': 'family', 'NIKHIL': 'unlimited' };
    return tierMap[tier] || 'free';
  };
  const currentPlanKey = getCurrentPlanKey();
  const hasActive = currentPlanKey !== 'free';
  // Which of the three paywalls (utils/membershipOffer); members see plain prices
  const kind = paywallKind({ subscriptionStatus, offer, isMember: hasActive });
  const [billingPeriod, setBillingPeriod] = useState(kind === 'normal' || kind === 'winback' ? 'monthly' : 'annual');
  useEffect(() => {
    setBillingPeriod(kind === 'normal' || kind === 'winback' ? 'monthly' : 'annual');
  }, [kind]);
  const trialConfig = subscriptionStatus?.trialConfig;
  const hasTrial = kind === 'trial';
  // Same two-page flow as the TrialWall: non-members with a trial see the
  // timeline first, members land straight on the plans.
  // 'timeline' (a trial's story) → 'plans' → 'checkout' (Stripe's embedded form, as on the funnel)
  const [screen, setScreen] = useState('timeline');
  const [checkout, setCheckout] = useState(null);   // { tier, billing, summary, experiment, offer, expect }
  const [checkoutBusy, setCheckoutBusy] = useState(false);   // a payment completing, or the hosted hand-off
  // The modal is a dialog: labelled by its headline, focused on opening (and
  // again when the checkout screen hands back), closed by Escape unless a
  // payment is completing
  const titleId = useId();
  const dialogRef = useRef(null);
  useEffect(() => { dialogRef.current?.focus(); }, []);
  const [offerChanged, setOfferChanged] = useState(false);   // the backend priced the last pick differently: re-priced plans
  const showTimeline = screen === 'timeline' && hasTrial;

  // ── the normal paywall ────────────────────────────────────────────────────
  // Priced from /subscription-plans exactly as the website wall is: Pro from
  // the funnel's fixed coupons (the first month in the experiment arm PostHog
  // dealt this visit, the first year $119.99), Family at the 45%. The plans
  // are fetched for members too, for the savings pill.
  const statusLoaded = subscriptionStatus?.isLoaded !== false;
  // win-back is monthly only: its annual cards are the normal paywall's (what checkout charges)
  const pricingOn = kind === 'normal' || (kind === 'winback' && billingPeriod === 'annual');
  const [plansData, setPlansData] = useState(null);
  const [plansError, setPlansError] = useState(false);
  const [plansTry, setPlansTry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setPlansError(false);
    Promise.resolve()
      .then(() => apiClient.getSubscriptionPlans())
      .then((r) => {
        if (cancelled) return;
        if (r?.status && r.data) setPlansData(r.data);
        else setPlansError(true);
      })
      .catch(() => {
        if (!cancelled) setPlansError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, plansTry]);
  const [chosenCents, setChosenCents] = useState(readChosenCents);
  const [chooserOpen, setChooserOpen] = useState(false);
  const prices = pricesFrom(plansData);
  // A plans response Stripe could not price is the same as no plans: the one notice, never a bare card
  const plansUnpriced = !!plansData && (!priceable(prices, 'pro') || !priceable(prices, 'family') || plansData.funnel_intro_offer?.available === false);
  const plansFailed = plansError || plansUnpriced;
  // the flag is given until a price is about to be shown (the plans screen, with the plans in)
  const offerVariant = useOfferVariant(pricingOn && statusLoaded && !showTimeline && !!plansData && !plansFailed);
  const savings = annualSavingsLabel(prices);
  // A sellable card's price, or why there is none yet: the normal paywall's
  // offer, or (members, offers off) Stripe's list price
  const cardFor = (offerTier) => {
    if (plansFailed) return { kind: 'unavailable' };
    if (!statusLoaded || !plansData) return { kind: 'loading' };
    return cardOffer(plansData, { tier: offerTier, billing: billingPeriod, variant: offerVariant, chosenCents, offersOn: pricingOn });
  };
  const priced = (card) => card?.kind === 'intro' || card?.kind === 'plain';
  // The win-back card: the backend's terms for this account (the coupon's
  // percent and months, each plan's first month, all read from Stripe); no
  // number of its own. Without terms for a plan there is no card to show.
  const winbackTerms = subscriptionStatus?.winbackOffer || null;
  const winbackCardFor = (offerTier) => {
    if (plansFailed) return { kind: 'unavailable' };
    if (!statusLoaded) return { kind: 'loading' };
    return winbackCard(winbackTerms, offerTier) || { kind: 'unavailable' };
  };
  const winbackPercent = winbackTerms?.percent_off ? Math.round(winbackTerms.percent_off) : null;
  const winbackMonths = Number(winbackTerms?.duration_months || 0);
  const proCard = cardFor('pro');
  const choose = pricingOn && billingPeriod === 'monthly' ? proCard?.choose || null : null;
  const named = proCard?.named ?? null;

  const planDescriptions = { free: 'Try Pixley', starter: 'For casual creators', plus: PLAN_COPY.pro.blurb, family: PLAN_COPY.family.blurb, unlimited: 'For frequent creators' };

  // The plans and their copy. No amounts live here: every price comes from
  // GET /subscription-plans (Stripe); legacy tiers (Starter / Max) are never
  // priced, they only ever show as the member's current plan.
  const plans = {
    free: { name: 'Free', starsPerWeek: 50, features: [{ text: 'Up to 1 min stories', on: true }, { text: '16:9 format only', on: true }, { text: 'English only', on: true }, { text: 'No downloads', on: false }], cta: 'Keep Free', disabled: true },
    starter: { name: 'Starter', starsPerWeek: 100, features: [{ text: 'Up to 1.5 min stories', on: true }, { text: '16:9 format only', on: true }, { text: 'English only', on: true }, { text: 'Watermarked downloads', on: true }], cta: 'Choose Starter' },
    // the sellable plans: the shared copy (utils/membershipOffer), the same words as the website wall
    plus: { name: PLAN_COPY.pro.name, starsPerWeek: PLAN_COPY.pro.starsPerWeek, features: PLAN_COPY.pro.features.map((text) => ({ text, on: true })), cta: 'Start Pro', badge: PLAN_COPY.pro.badge },
    family: { name: PLAN_COPY.family.name, starsPerWeek: PLAN_COPY.family.starsPerWeek, features: PLAN_COPY.family.features.map((text) => ({ text, on: true })), cta: 'Choose Family', badge: PLAN_COPY.family.badge },
    unlimited: { name: 'Max', starsPerWeek: 1500, features: [{ text: 'Up to 3 min stories', on: true }, { text: 'All formats', on: true }, { text: 'All languages', on: true }, { text: 'Download videos', on: true }, { text: 'No watermark', on: true }], cta: 'Choose Max', badge: 'BEST VALUE' },
  };

  const handleSubscribe = async (tier) => {
    const executeSubscribe = async () => {
      if (!user?.id || !user?.email) { showToast("Unable to process. Try logging out and back in.", "error"); return; }
      if (isProcessing) return;
      const hasActive = currentPlanKey !== 'free';

      if (hasActive && tier === 'free') {
        if (subscriptionStatus?.managementPlatform === 'app_store') {
          window.open('https://apps.apple.com/account/subscriptions', '_blank', 'noopener,noreferrer');
          return;
        }
        if (!window.confirm("Cancel your subscription? You'll keep access until end of billing period.")) return;
        try { setIsProcessing(true); const r = await apiClient.createStripePortalSession(user.id); if (r.status && r.data.portal_url) window.location.href = r.data.portal_url; else throw new Error(r.error); } catch (e) { showToast("Failed to open portal.", "error"); setIsProcessing(false); } return;
      }
      // Web-offer plans (RevenueCat Billing) are changed and cancelled on
      // their own billing page, not through our Stripe plan-change API.
      if (hasActive && subscriptionStatus?.managementPlatform === 'rc_billing') {
        const msg = tier === 'free'
          ? "We'll open your billing page, where you can cancel. You keep access until the end of your billing period."
          : "Your plan is billed through our web checkout. We'll open your billing page, where you can change or cancel your plan.";
        if (!window.confirm(msg)) return;
        try {
          setIsProcessing(true);
          const r = await apiClient.createStripePortalSession(user.id);
          if (r.status && r.data?.portal_url) window.location.href = r.data.portal_url;
          else throw new Error(r.error);
        } catch (e) { showToast(e?.message || "Couldn't open your billing page. Please try again.", "error"); setIsProcessing(false); }
        return;
      }
      if (hasActive && tier !== 'free') {
        const tierH = ['free','starter','plus','family','unlimited'];
        const isUp = tierH.indexOf(tier) > tierH.indexOf(currentPlanKey);
        if (!window.confirm(isUp ? "Upgrade? You'll be charged a prorated amount." : "Downgrade? Takes effect immediately.")) return;
        try {
          setIsProcessing(true);
          const r = await apiClient.updateStripeSubscription(user.id, tier, billingPeriod);
          if (r.status) {
            showToast(`${isUp ? 'Upgraded' : 'Downgraded'}! Updating your plan...`, "success");
            // Poll until RevenueCat reflects the tier change so the reload
            // lands on a fresh cache instead of the old tier (30-min TTL).
            await waitForSubscriptionActivation({
              untilChangedFrom: subscriptionStatus?.subscriptionType,
              timeoutMs: 20000,
              intervalMs: 2500,
            });
            window.location.reload();
          } else throw new Error(r.error);
        } catch (e) { showToast(e?.message || "Failed to update subscription.", "error"); setIsProcessing(false); } return;
      }
      const offerTier = tier === 'plus' ? 'pro' : tier;
      const card = cardFor(offerTier);
      const winback = kind === 'winback' && billingPeriod === 'monthly';
      const shown = winback ? winbackCardFor(offerTier) : card;
      if (!priced(shown)) return;   // no price was shown, so nothing is charged
      // the normal paywall: the surface, the arm Pro was priced in, and the
      // first-month price named in the pwyw arm (the backend prices from its own table)
      const experiment = pricingOn
        ? { surface: 'subscription_modal', offerVariant: offerVariant || null, chosenCents: offerTier === 'pro' && billingPeriod === 'monthly' ? named : null }
        : null;
      trackCheckoutStarted('subscription', tier, experiment ? offerAnalytics(plansData, { surface: 'subscription_modal', variant: offerVariant, card }) : {});
      const plan = plans[tier];
      const summary = winback
        ? { name: plan.name, chip: shown.chip || null, today: shown.discountedPrice, renewal: shown.months > 1 ? `${shown.discountedPrice}/month for ${shown.months} months, then ${shown.renewalPrice}` : `Then ${shown.renewalPrice}` }
        : hasTrial
        ? { name: plan.name, chip: `${trialConfig.duration_days}-day free trial`, today: '$0.00', renewal: `Then ${shown.renewalPrice} after your trial` }
        : shown.kind === 'intro'
        ? { name: plan.name, chip: shown.chip || null, today: shown.discountedPrice, renewal: `Then ${shown.renewalPrice}` }
        : { name: plan.name, chip: null, today: shown.regularPrice, renewal: `Then ${shown.renewalPrice}` };
      // Stripe's embedded form, inside the modal (components/MembershipCheckout);
      // `expect` is what this card promised, checked against the session the backend makes
      const expect = {
        kind: winback ? 'winback' : hasTrial ? 'trial' : 'normal',
        offerVariant: pricingOn && offerTier === 'pro' ? servedOfferVariant(plansData, offerVariant) : null,
        chosenCents: experiment?.chosenCents ?? null,
      };
      setOfferChanged(false);
      setCheckout({ tier, billing: billingPeriod, summary, experiment, offer: winback ? 'winback' : null, expect });
      setScreen('checkout');
    };
    const hasActive = currentPlanKey !== 'free';
    const action = hasActive && tier === 'free' ? 'cancel subscription' : !hasActive ? 'subscribe to premium' : 'change subscription';
    requireParentalVerification(action, executeSubscribe);
  };

  const renderPlanCard = (planKey, plan) => {
    const isCurrentPlan = planKey === currentPlanKey;
    const isHighlighted = plan.badge === 'MOST POPULAR';
    const offerTier = planKey === 'plus' ? 'pro' : planKey;
    const tierH = ['free','starter','plus','family','unlimited'];
    const isDown = tierH.indexOf(planKey) < tierH.indexOf(currentPlanKey);
    const isUp = tierH.indexOf(planKey) > tierH.indexOf(currentPlanKey);
    // From the win-back email, monthly plans are priced with the win-back
    // coupon checkout applies (33% off each of the first 3 monthly invoices,
    // on Stripe's monthly price), never the new-customer offer — the card
    // must show what they'll pay.
    const winbackPrice = kind === 'winback' && billingPeriod === 'monthly' && ['plus', 'family'].includes(planKey);
    // the normal paywall: this card priced from the plans response (sellable plans only)
    const card = winbackPrice
      ? winbackCardFor(offerTier)
      : ['plus', 'family'].includes(planKey) ? cardFor(offerTier) : null;
    const waiting = card && !priced(card);
    const showIntroPrice = card?.kind === 'intro';
    const introPricing = card?.kind === 'intro' ? card : null;
    const introPeriodLine = winbackPrice && introPricing?.months > 1 ? `a month for your ${introPricing.periodLabel}` : `for your ${introPricing?.periodLabel}`;
    const priceAction = !winbackPrice && card?.kind === 'intro' && card.choose ? {
      label: Number.isInteger(card.named) ? 'Change your price' : 'Choose your price',
      hint: Number.isInteger(card.named) ? 'Your price \u00b7 tap to change' : 'Tap to set your price',
      chosen: Number.isInteger(card.named),
      onClick: () => setChooserOpen(true),
    } : null;

    let ctaText = plan.cta;
    let isDisabled = false;
    if (isCurrentPlan) { ctaText = ['starter','unlimited'].includes(planKey) ? 'Your plan (legacy)' : 'Current Plan'; isDisabled = true; }
    else if (hasActive && isDown) { ctaText = planKey === 'free' ? 'Cancel' : 'Downgrade'; }
    else if (hasActive && isUp) { ctaText = 'Upgrade'; }
    else if (waiting) { ctaText = 'Loading price…'; isDisabled = true; }
    else if (showIntroPrice) { ctaText = `Start ${plan.name} — ${introPricing.discountedPrice}${winbackPrice && introPricing.months > 1 ? '/mo' : ''}`; }

    return (
      <div key={planKey} style={{ position: "relative", display: "flex", flexDirection: "column" }}>
        {plan.badge && (
          <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
            <span style={{ padding: "5px 14px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#FFF", background: plan.badge === 'MOST POPULAR' ? "#0065F8" : "#FF6B9A", boxShadow: "0px 3px 0px " + (plan.badge === 'MOST POPULAR' ? "#073B8E" : "#C94D73"), whiteSpace: "nowrap" }}>{plan.badge}</span>
          </div>
        )}

        <div style={{
          flex: 1, borderRadius: 20, padding: "28px 20px 20px", display: "flex", flexDirection: "column",
          background: "#FFFFFF",
          border: isHighlighted ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
          boxShadow: isHighlighted ? "0px 4px 0px rgba(0,101,248,0.15), 0px 12px 32px rgba(0,101,248,0.10)" : "0px 2px 0px rgba(11,26,51,0.04)",
          fontFamily: "'Inter', 'Fredoka', sans-serif",
        }}>
          {/* Plan name */}
          <h3 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: "#0065F8", textAlign: "center", margin: 0, textTransform: "uppercase" }}>{plan.name}</h3>
          <p style={{ fontSize: 12, color: "#334155", textAlign: "center", margin: "2px 0 12px" }}>{planDescriptions[planKey]}</p>

          {/* Price */}
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            {planKey === 'free' ? (
              <>
                <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 42, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>$0</div>
                <div style={{ fontSize: 12, color: "transparent", marginTop: 4, userSelect: "none" }}>&nbsp;<br/>&nbsp;</div>
              </>
            ) : (
              <>
                {waiting ? (
                  <div role="status" aria-live="polite" aria-busy="true" style={{ height: 118, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span aria-hidden="true" style={{ display: "inline-block", width: 22, height: 22, borderRadius: "50%", border: "3px solid #D7E3F3", borderTopColor: "#0065F8", animation: "tw-spin 0.9s linear infinite" }} />
                    <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Loading price…</span>
                  </div>
                ) : showIntroPrice ? (
                  <>
                    {introPricing.chip && (
                      <div style={{ display: "inline-flex", alignItems: "center", padding: "7px 16px", borderRadius: 999, background: "#E8F2FF", color: "#0065F8", fontFamily: "'Fredoka', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 12 }}>
                        {introPricing.chip}
                      </div>
                    )}
                    {(() => {
                      const row = (
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: billingPeriod === 'annual' ? 12 : 16, flexWrap: "nowrap", whiteSpace: "nowrap", width: priceAction ? undefined : "100%" }}>
                          {introPricing.discountPercent > 0 && (
                            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: billingPeriod === 'annual' ? 34 : 40, fontWeight: 700, color: "#8A99B3", lineHeight: 1, letterSpacing: "-0.02em", textDecoration: "line-through", textDecorationThickness: 3 }}>
                              {introPricing.regularPrice}
                            </span>
                          )}
                          <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: billingPeriod === 'annual' ? 34 : 40, fontWeight: 700, color: "#0B1A33", lineHeight: 1, letterSpacing: "-0.02em" }}>
                            {introPricing.discountedPrice}
                          </span>
                        </div>
                      );
                      return priceAction ? <><div><PriceField action={priceAction}>{row}</PriceField></div><PriceHint action={priceAction} /></> : row;
                    })()}
                    <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 15, fontWeight: 600, color: "#334155", marginTop: 5 }}>
                      {introPeriodLine}
                    </div>
                    <div style={{ fontSize: 12, color: "#5A6B85", marginTop: 5 }}>
                      Then {introPricing.renewalPrice} · cancel anytime
                    </div>
                  </>
                ) : (
                  card?.kind === 'plain' ? (
                    // the list price, from Stripe (members, or offers off)
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 3 }}>
                        <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 42, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>{card.monthlyEquivalent}</span>
                        <span style={{ fontSize: 14, color: "#334155", fontWeight: 500 }}>/mo</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>
                        {billingPeriod === 'annual' && Number.isInteger(prices[offerTier]?.monthly) ? (
                          <><span style={{ textDecoration: "line-through" }}>{formatCents(prices[offerTier].monthly)} monthly</span><br/>billed annually</>
                        ) : billingPeriod === 'annual' ? 'Billed annually' : 'Billed monthly'}
                      </div>
                    </>
                  ) : (
                    // a legacy tier (Starter / Max): never priced, only ever the member's current plan
                    <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 18, fontWeight: 700, color: "#334155", padding: "12px 0 14px" }}>Your current plan</div>
                  )
                )}
              </>
            )}
          </div>

          {/* Stars */}
          <div style={{ padding: "10px 12px", borderRadius: 14, background: "#EAF3FF", border: "1px solid rgba(0,101,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
            <img src="/assets/pixley_stars.png" alt="Stars" style={{ width: 20, height: 20 }} onError={(e) => { e.target.style.display = 'none'; }} />
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 18, fontWeight: 700, color: "#0065F8" }}>{plan.starsPerWeek}</span>
            <span style={{ fontSize: 12, color: "#334155" }}>stars / <strong style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, color: "#0065F8" }}>week</strong></span>
          </div>

          {/* Features */}
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
            {plan.features.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#0B1A33", fontWeight: 400 }}>
                <span style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, background: f.on ? "#E7F0FF" : "#F0F4FA", color: f.on ? "#0065F8" : "#C3D0E2", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {f.on ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5" /></svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"><path d="M5 12h14" /></svg>
                  )}
                </span>
                <span style={{ color: f.on ? "#0B1A33" : "#334155" }}>{f.text}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button
            onClick={() => !isDisabled && handleSubscribe(planKey)}
            disabled={isDisabled || isProcessing}
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: isDisabled ? "not-allowed" : "pointer",
              fontFamily: "'Fredoka', sans-serif",
              background: isDisabled ? "#EAF3FF" : isHighlighted ? "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)" : "#FFFFFF",
              color: isDisabled ? "#8CA0BC" : isHighlighted ? "#FFFFFF" : "#0065F8",
              boxShadow: isDisabled ? "none" : isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)",
              border: isDisabled || isHighlighted ? "none" : "2px solid #0065F8",
              transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
            }}
            onMouseDown={(e) => { if (!isDisabled) { e.currentTarget.style.transform = "translateY(3px)"; e.currentTarget.style.boxShadow = "0px 0px 0px transparent"; } }}
            onMouseUp={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)"; }}
          >
            {isProcessing ? 'Processing...' : ctaText}
          </button>
        </div>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-2 sm:p-4" style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}>
      <div onClick={checkoutBusy ? undefined : onClose} className="absolute inset-0" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !checkoutBusy && !chooserOpen) onClose?.();
        }}
        style={{
        outline: "none",
        position: "relative", width: "100%", maxWidth: showTimeline || screen === 'checkout' ? 560 : 1200, minHeight: "min(700px, 88vh)", maxHeight: "92vh", transition: "max-width 300ms ease",
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        borderRadius: 28,
        boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
        overflow: "hidden", display: "flex", flexDirection: "column",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}>
        {/* Decorative background shapes */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          <div style={{ position: "absolute", bottom: -80, right: -80, width: 260, height: 260, borderRadius: "50%", background: "rgba(0,202,255,0.05)" }} />
          <div style={{ position: "absolute", top: "40%", left: "10%", width: 120, height: 120, borderRadius: "50%", background: "rgba(127,198,255,0.04)" }} />
        </div>

        {/* Back to the trial timeline */}
        {((hasTrial && !showTimeline) || screen === 'checkout') && (
          <button onClick={() => { if (screen === 'checkout') { setCheckout(null); setScreen('plans'); } else setScreen('timeline'); dialogRef.current?.focus(); }} disabled={checkoutBusy} aria-label="Back" style={{ position: "absolute", top: 16, left: 16, zIndex: 10, width: 44, height: 44, borderRadius: 14, background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)", display: "flex", alignItems: "center", justifyContent: "center", color: "#334155" }}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Close */}
        <button onClick={onClose} disabled={checkoutBusy} aria-label="Close" style={{ position: "absolute", top: 16, right: 16, zIndex: 10, width: 44, height: 44, borderRadius: 14, background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#334155" }}>
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div style={{ position: "relative", padding: "24px 24px 20px", overflowY: "auto" }}>
          <style>{`
            @keyframes submodal-confetti-fall {
              0% { top: -6%; transform: rotate(0deg); opacity: 1; }
              80% { opacity: 1; }
              100% { top: 104%; transform: rotate(420deg); opacity: 0; }
            }
            @keyframes tw-step-in {
              0% { opacity: 0; transform: translateY(12px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes tw-chip-pop {
              0% { transform: scale(0.3); }
              70% { transform: scale(1.18); }
              100% { transform: scale(1); }
            }
            @keyframes tw-rail-grow {
              0% { transform: scaleY(0); }
              100% { transform: scaleY(1); }
            }
            @keyframes tw-screen-in {
              0% { opacity: 0; transform: translateX(36px); }
              100% { opacity: 1; transform: translateX(0); }
            }
            @keyframes tw-pill-in {
              0% { opacity: 0; transform: translateY(6px) scale(0.94); }
              60% { opacity: 1; transform: translateY(-1px) scale(1.03); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes tw-field-pulse {
              0% { box-shadow: 0 0 0 0 rgba(0,101,248,0.35); }
              70% { box-shadow: 0 0 0 12px rgba(0,101,248,0); }
              100% { box-shadow: 0 0 0 0 rgba(0,101,248,0); }
            }
            @keyframes tw-spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
          {/* Header — Pip hero (matches the TrialWall) */}
          <div style={{ position: "relative", textAlign: "center", margin: "-24px -24px 20px", overflow: "hidden", background: "radial-gradient(120% 100% at 50% 0%, #DCEBFF 0%, rgba(220,235,255,0) 78%)", padding: "30px 24px 8px" }}>
            {["#0065F8", "#FF6B9A", "#FFC93C", "#37D67A", "#8A5CF6", "#00CAFF"].map((color, i) => (
              <span
                key={i}
                aria-hidden="true"
                style={{
                  position: "absolute", top: "-6%", left: `${(i * 31 + 9) % 100}%`,
                  width: 7 + (i % 3) * 3, height: 4,
                  background: color, borderRadius: 2,
                  transform: `rotate(${(i * 53) % 360}deg)`,
                  animation: `submodal-confetti-fall ${3.4 + (i % 4) * 0.5}s linear ${(i % 5) * 0.6}s infinite`,
                  pointerEvents: "none", opacity: 0.85,
                }}
              />
            ))}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, position: "relative" }}>
              <img src="/assets/pixley-logo-full.png" alt="Pixley" style={{ height: 96, objectFit: "contain" }} />
            </div>
            <h2 id={titleId} style={{ fontFamily: "'Fredoka', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, color: "#0B1A33", margin: "0 0 6px", lineHeight: 1.15, position: "relative" }}>
              Choose the right plan for your family
            </h2>
            <p style={{ fontSize: 14.5, color: "#5A6B85", margin: 0, position: "relative" }}>More stars, longer stories, and premium tools for every kind of creator.</p>
            {kind === 'winback' && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginTop: 14, padding: "8px 16px 8px 8px", borderRadius: 999, background: "#FFF6DC", border: "2px solid rgba(232,148,16,0.25)", position: "relative" }}>
                <img src="/assets/cancel/pip_heart.webp" alt="" style={{ width: 34, height: 34, objectFit: "contain" }} />
                <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14.5, fontWeight: 700, color: "#0B1A33" }}>
                  Welcome back! {winbackPercent ? `${winbackPercent}% off` : 'A special price'} {winbackMonths > 1 ? `for your first ${winbackMonths} months` : 'for your first month'} on monthly plans.
                </span>
              </div>
            )}
          </div>

          {screen === 'checkout' && checkout ? (
            <div key="checkout" style={{ animation: "tw-screen-in 0.4s ease-out both", paddingTop: 4 }}>
              <MembershipCheckout
                apiClient={apiClient}
                user={user}
                tier={checkout.tier}
                billing={checkout.billing}
                summary={checkout.summary}
                experiment={checkout.experiment}
                offer={checkout.offer}
                surface="subscription_modal"
                expect={checkout.expect}
                publishableKey={plansData?.stripe_publishable_key || null}
                returnPath={window.location.pathname + window.location.search}
                onBack={() => { setCheckout(null); setScreen('plans'); dialogRef.current?.focus(); }}
                onBusyChange={setCheckoutBusy}
                onOfferChanged={() => {
                  // the backend priced it differently from the card: back to freshly priced plans
                  setCheckout(null);
                  setScreen('plans');
                  setOfferChanged(true);
                  setPlansData(null);
                  setPlansTry((n) => n + 1);
                  refreshSubscriptionStatus?.();
                }}
              />
            </div>
          ) : showTimeline ? (
            <div key="timeline" style={{ animation: "tw-screen-in 0.4s ease-out both" }}>
              <TrialTimeline trialConfig={trialConfig} />
              <div style={{ animation: "tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.5s both", marginBottom: 18 }}>
                <ReviewCard />
              </div>
              <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", animation: "tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.7s both" }}>
                <button
                  onClick={() => setScreen('plans')}
                  style={{ fontFamily: "'Fredoka', sans-serif", width: "100%", padding: "14px 0", borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: "pointer", color: "#FFFFFF", background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", border: "1px solid rgba(255,255,255,0.5)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0px 4px 0px #073B8E" }}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
          <div key="plans" style={{ animation: "tw-screen-in 0.4s ease-out both" }}>
          {offerChanged && !plansFailed && (
            <p role="status" style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14.5, fontWeight: 600, color: "#0065F8", textAlign: "center", margin: "0 0 16px" }}>
              Your offer has changed, so here are today's prices.
            </p>
          )}
          {/* Billing toggle (nothing to switch while the plans could not load) */}
          {!plansFailed && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 28 }}>
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14, fontWeight: 700, color: billingPeriod === 'monthly' ? "#0B1A33" : "#8CA0BC" }}>Monthly</span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              aria-label="Toggle billing period"
              aria-pressed={billingPeriod === 'annual'}
              style={{ position: "relative", width: 56, height: 30, borderRadius: 999, background: billingPeriod === 'annual' ? "#0065F8" : "#D7E3F3", border: "none", cursor: "pointer", transition: "background 200ms ease", boxShadow: "inset 0 2px 4px rgba(11,26,51,0.1)" }}
            >
              <div style={{ position: "absolute", top: 3, left: billingPeriod === 'annual' ? 29 : 3, width: 24, height: 24, borderRadius: "50%", background: "#FFF", boxShadow: "0px 2px 4px rgba(11,26,51,0.2)", transition: "left 200ms ease" }} />
            </button>
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14, fontWeight: 700, color: billingPeriod === 'annual' ? "#0B1A33" : "#8CA0BC" }}>Annual</span>
            {savings && (
              <span style={{ padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: "#0065F8", color: "#FFF", boxShadow: "0px 2px 0px #073B8E", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{savings}</span>
            )}
          </div>
          )}

          {/* Plans grid — members-only model: Pro + Family are the sellable
              plans. The Free card is gone; Starter/Max render ONLY as the
              user's current (grandfathered) plan, never as a choice. */}
          {plansFailed ? (
            // prices could not be read: one friendly notice in place of the cards, never a guessed number
            <div role="alert" style={{ textAlign: "center", padding: "28px 20px", margin: "0 auto 24px", maxWidth: 560, background: "#FFFFFF", borderRadius: 20, border: "2px solid rgba(11,26,51,0.08)" }}>
              <p style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 17, fontWeight: 700, color: "#0B1A33", margin: "0 0 6px" }}>We couldn't load our plans just now</p>
              <p style={{ fontSize: 13.5, color: "#5A6B85", margin: "0 0 16px", lineHeight: 1.5 }}>Please check your connection and try again in a moment.</p>
              <button
                type="button"
                onClick={() => { setPlansData(null); setPlansTry((n) => n + 1); }}
                style={{ fontFamily: "'Fredoka', sans-serif", padding: "11px 26px", borderRadius: 14, fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#FFFFFF", background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", border: "1px solid rgba(255,255,255,0.5)", boxShadow: "0px 4px 0px #073B8E" }}
              >
                Try again
              </button>
            </div>
          ) : (() => {
            const sellable = ['plus', 'family'];
            const visible = ['starter', 'plus', 'family', 'unlimited'].filter(
              (k) => sellable.includes(k) || k === currentPlanKey,
            );
            return (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${visible.length}, 1fr)`, gap: 14, marginBottom: 24, maxWidth: visible.length <= 2 ? 560 : undefined, marginLeft: "auto", marginRight: "auto" }} className="subscription-plans-grid">
                {visible.map((k) => renderPlanCard(k, plans[k]))}
              </div>
            );
          })()}

          {/* Cancel path (was the Free card's role) for active subscribers */}
          {currentPlanKey !== 'free' && ['stripe', 'rc_billing'].includes(subscriptionStatus?.managementPlatform) && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <button
                onClick={() => setShowCancelFlow(true)}
                style={{ background: "none", border: "none", color: "#8A99B3", fontSize: 12.5, textDecoration: "underline", cursor: "pointer" }}
              >
                Cancel subscription
              </button>
            </div>
          )}
          {currentPlanKey !== 'free' && subscriptionStatus?.managementPlatform === 'app_store' && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <a
                href="https://apps.apple.com/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0065F8", fontSize: 12.5, fontWeight: 600 }}
              >
                Manage subscription in the App Store
              </a>
            </div>
          )}

          {/* Footer */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
              <span>Secure checkout</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              <span>Restore purchases</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 16v-4m0-4h.01" /></svg>
              <span>Subscriptions renew automatically unless canceled.</span>
            </div>
          </div>
          </div>
          )}
        </div>
      </div>

      {choose && (
        <FunnelPriceChooser
          open={chooserOpen}
          choose={choose}
          valueCents={Number.isInteger(named) ? named : choose.suggested_cents}
          onConfirm={(cents) => {
            setChosenCents(cents);
            writeChosenCents(cents);
            setChooserOpen(false);
            dialogRef.current?.focus();
          }}
          onClose={() => { setChooserOpen(false); dialogRef.current?.focus(); }}
          zIndex={100}
        />
      )}

      <CancelFlow
        isOpen={showCancelFlow}
        onClose={() => setShowCancelFlow(false)}
        onChanged={() => { if (typeof onClose === 'function') onClose(); }}
      />

      <style>{`
        @media (max-width: 900px) {
          .subscription-plans-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 500px) {
          .subscription-plans-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>,
    document.body
  );
};

// Mounted on several pages and open rarely: the modal's hooks (the plans
// fetch, the experiment flag) run only while it is open, so the arm is read
// when a parent is about to see a price, not at page load.
const SubscriptionModal = ({ isOpen, ...props }) => (isOpen ? <OpenSubscriptionModal {...props} /> : null);

export default SubscriptionModal;
